import React, { FC, useCallback, useMemo } from "react";
import { ContentTabType } from "@src/components/contentLibrary/hooks/useContentQueryParams";
import { textVariants } from "@ui/text";
import { useModal } from "@hooks/useModal";
import LibraryItemDetailPageHeader from "../LibraryItemDetailPageHeader";
import ContentAvatar from "@src/deprecatedDesignSystem/components/ContentAvatar";
import { ContentType } from "@src/deprecatedDesignSystem/components/ContentExplainerCard";
import { useToast } from "@src/hooks/useToast";
import useActionPermissions from "@src/hooks/useActionPermissions";
import { CONTENT_LIBRARY_REFETCH_QUERIES } from "@src/contexts/TagContext/TagContext";
import ArchiveConfirmationModal from "@src/components/modals/ArchiveConfirmationModal";
import {
  GraphqlOperations,
  PublishState,
  TrainingResourceVersionType,
} from "@src/types.generated";
import { useTracking } from "@src/hooks/useTracking";
import { TrainingResourceLibraryItem } from "./TrainingResourceDetailPage";
import {
  currentStateIsPublishedAtom,
  selectedTrainingResourceIdAtom,
  trainingResourceTitleAtom,
} from "./atoms";
import { route } from "nextjs-routes";
import { useLibraryItemQueryParams } from "../contexts/LibraryItemQueryParamsContext";
import { cn } from "@src/ui/lib/utils";
import { openDownload } from "@src/utils/csv";
import { launchConfetti } from "@src/utils/confetti";
import { useArchiveTrainingResourceMutation } from "@src/components/libraryItemDetailPages/LibraryItemDetailPageContainer.generated";
import ArchiveOutlineIcon from "@src/ui/icons/18px/archive-outline";
import { DropdownMenuItem } from "@src/ui/dropdown-menu";
import ArrowRotateCounterclockwiseIcon from "@src/ui/icons/18px/arrow-rotate-counterclockwise";
import FileArrowInIcon from "@src/ui/icons/18px/file-arrow-in";
import GearOutlineIcon from "@src/ui/icons/18px/gear-outline";
import { Button } from "@ui/button";
import ArrowDownloadIcon from "@src/ui/icons/18px/arrow-download";
import { usePublishTrainingResourceMutation } from "./TrainingResourcePageHeader.generated";
import EditLibraryItemModal, {
  EditLibraryItemModalData,
} from "../module/EditLibraryItemModal";
import { updateTranslationSet } from "@src/utils/translationSets";
import useTranslationModeContext from "@src/components/libraryItemDetailPages/course/hooks/useTranslationModeContext";
import { useAvailableLanguages } from "@src/hooks/useAvailableLanguages";
import { PUBLISH_STATE_COLOR } from "@src/utils/courses";
import Pill from "@src/deprecatedDesignSystem/components/Pill";
import { PUBLISH_STATE_COPY } from "@src/utils/courses";
import useContentPermissions from "../hooks/useContentPermissions";
import { ContentPermissionsFragment } from "../hooks/useContentPermissions.generated";
import { useAtomValue } from "jotai";
import { TrainingResourceVersionFragment } from "@src/fragments.generated";
import { useUpdateTrainingResourceMutation } from "@src/components/modals/TrainingResourceModal.generated";
import { gql } from "@apollo/client";
import { pathVersionPathIdAtom } from "../module/atoms";
import TrainingResourceVersionHistoryDropdown from "@src/components/libraryItemDetailPages/resource/TrainingResourceVersionHistoryDropdown";
import LockedTrainingResourceVersionBanner from "@src/components/libraryItemDetailPages/resource/LockedTrainingResourceVersionBanner";
import useVersionQueryParams from "@src/components/libraryItemDetailPages/hooks/useVersionQueryParams";
import UndoRedoButtons from "@src/components/libraryItemDetailPages/shared/UndoRedoButtons";
type Props = {
  libraryItem: TrainingResourceLibraryItem;
  trainingResourceVersion?: TrainingResourceVersionFragment;
};

export const useTrainingResourceContentPermissions = ():
  | ContentPermissionsFragment
  | undefined => {
  const trainingResourceId = useAtomValue(selectedTrainingResourceIdAtom);
  return useContentPermissions({
    trainingResourceId,
  });
};

const TrainingResourcePageHeader: FC<Props> = (props) => {
  const { setManageTab, selectedMainTab } = useLibraryItemQueryParams();
  const { version } = useVersionQueryParams();
  const { showModal, closeModal } = useModal();
  const { userActionPermissions } = useActionPermissions();
  const translationModeContext = useTranslationModeContext();
  const { trackEvent } = useTracking();
  const { addErrorToast, addToast } = useToast();
  const currentStateIsPublished = useAtomValue(currentStateIsPublishedAtom);
  const [archiveTrainingResource] = useArchiveTrainingResourceMutation({
    refetchQueries: CONTENT_LIBRARY_REFETCH_QUERIES,
    onCompleted: (data) => {
      if (!data.archiveTrainingResource.success) {
        addErrorToast({
          ...data.archiveTrainingResource,
          callsite: "drag_training_resource_archive_training_resource",
        });
      }
    },
  });
  const { allLanguages: langs } = useAvailableLanguages();
  const [updateTrainingResource] = useUpdateTrainingResourceMutation({
    refetchQueries: [GraphqlOperations.Query.LibraryItemDetailPage_LibraryItem],
  });
  const trainingResourceId = props.libraryItem.trainingResource?.id;

  const trainingResourceTitle = useAtomValue(trainingResourceTitleAtom);

  const onSave = useCallback(
    (data: EditLibraryItemModalData) => {
      if (!translationModeContext) {
        throw new Error("No translation mode context");
      }
      updateTrainingResource({
        variables: {
          trainingResourceId: props.libraryItem.trainingResource?.id,
          input: {
            name: updateTranslationSet({
              lang: translationModeContext.currentLang,
              text: data.name,
              ts: props.libraryItem.name,
            }),
            description: updateTranslationSet({
              lang: translationModeContext.currentLang,
              text: data.description,
              ts: props.libraryItem.description,
            }),
          },
        },
      });
      closeModal();
    },
    [
      closeModal,
      props.libraryItem.description,
      props.libraryItem.name,
      props.libraryItem.trainingResource?.id,
      translationModeContext,
      updateTrainingResource,
    ],
  );
  const editTrainingResourceNameAndDescription = useCallback(() => {
    showModal(
      <EditLibraryItemModal
        modalHeader="Edit Resource"
        name={trainingResourceTitle?.en || "Untitled Resource"}
        description={props.libraryItem.description.en}
        onSave={onSave}
        coverImage={props.libraryItem.coverImage}
        disableCoverImageEditing
      />,
    );
  }, [
    onSave,
    props.libraryItem.description.en,
    trainingResourceTitle,
    props.libraryItem.coverImage,
    showModal,
  ]);
  const permissions = useTrainingResourceContentPermissions();
  const goToSettingsTab = useCallback(() => {
    setManageTab("settings");
  }, [setManageTab]);
  const media = props.libraryItem.trainingResource.draftVersion.media;
  const trainingResourceVersionId =
    props.libraryItem.trainingResource.draftVersion?.id;

  const handleExportPDF = useCallback(() => {
    if (!trainingResourceId) return;
    window.open(
      route({
        pathname: "/training-resource/[id]/export",
        query: {
          id: trainingResourceId,
          lang: translationModeContext?.currentLang || "en",
          version,
        },
      }),
    );
  }, [trainingResourceId, translationModeContext?.currentLang, version]);

  const handleDownloadMedia = useCallback(() => {
    const mediaUrl =
      props.libraryItem.trainingResource?.publishedVersion?.media
        ?.unoptimizedUrl ||
      props.libraryItem.trainingResource?.publishedVersion?.media?.mediaUrls.en;
    if (!mediaUrl) {
      return;
    }
    openDownload(mediaUrl, props.libraryItem.name.en);
    trackEvent("download_training_resource", {
      id: props.libraryItem.trainingResource.id,
    });
  }, [
    props.libraryItem.name.en,
    props.libraryItem.trainingResource,
    trackEvent,
  ]);

  const handleDownloadOrExport = useCallback(() => {
    const isRichTextDocument =
      props.libraryItem.trainingResource.draftVersion.richTextDocument;

    isRichTextDocument ? handleExportPDF() : handleDownloadMedia();
  }, [
    props.libraryItem.trainingResource.draftVersion.richTextDocument,
    handleExportPDF,
    handleDownloadMedia,
  ]);

  const [publishTrainingResource] = usePublishTrainingResourceMutation({
    refetchQueries: [GraphqlOperations.Query.LibraryItemDetailPage_LibraryItem],
    onCompleted: (data) => {
      if (data.publishTrainingResource.success) {
        launchConfetti();
        addToast({ message: "Resource published successfully" });
      } else {
        addErrorToast({
          ...data,
          message: "Failed to publish resource",
          callsite: "publish_training_resource",
        });
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        message: "Failed to publish resource",
        callsite: "publish_training_resource",
      });
    },
  });
  const handlePublishTrainingResource = useCallback(() => {
    if (
      !trainingResourceId ||
      !trainingResourceVersionId ||
      !permissions?.hasPublishPermission
    ) {
      addErrorToast({
        message: "You do not have permission to publish this resource",
        callsite: "publish_training_resource",
      });
      return;
    }
    publishTrainingResource({
      variables: {
        trainingResourceId,
      },
    });
  }, [
    addErrorToast,
    permissions?.hasPublishPermission,
    publishTrainingResource,
    trainingResourceId,
    trainingResourceVersionId,
  ]);
  const publishState: PublishState = useMemo(() => {
    return (
      props.trainingResourceVersion?.trainingResource?.libraryItem
        ?.publishState ?? PublishState.Draft
    );
  }, [
    props.trainingResourceVersion?.trainingResource?.libraryItem?.publishState,
  ]);
  const inModuleBuilder = !!useAtomValue(pathVersionPathIdAtom);
  const shouldHidePublishButton = useMemo(() => {
    if (inModuleBuilder) {
      return false;
    }
    return (
      props.trainingResourceVersion?.type ===
        TrainingResourceVersionType.Media &&
      props.trainingResourceVersion?.trainingResource?.libraryItem
        ?.publishState === PublishState.Published
    );
  }, [
    inModuleBuilder,
    props.trainingResourceVersion?.trainingResource?.libraryItem?.publishState,
    props.trainingResourceVersion?.type,
  ]);
  return (
    <div className="flex flex-col self-stretch">
      <LibraryItemDetailPageHeader
        libraryItem={props.libraryItem}
        hidePublishButton={shouldHidePublishButton}
        titleComponent={
          <div className="flex min-w-48 items-center gap-2">
            <ContentAvatar
              size="32px"
              contentType={ContentType.FILE}
              style={{ borderRadius: 10 }}
              defaultBackgroundHashKey={props.libraryItem.id}
              media={media}
            />
            <div className="flex min-w-48 flex-col">
              <p
                className={cn(
                  textVariants({ variant: "P2" }),
                  "truncate font-medium",
                )}
              >
                {trainingResourceTitle?.en || "Untitled Resource"}
              </p>
              <div className="flex items-center gap-2">
                <p
                  className={cn(
                    textVariants({ variant: "P3" }),
                    "font-medium text-gray-8",
                  )}
                >
                  Resource
                </p>
                <Pill
                  title={
                    props.libraryItem.archivedAt
                      ? "Archived"
                      : PUBLISH_STATE_COPY[publishState]
                  }
                  color={
                    props.libraryItem.archivedAt
                      ? "gray"
                      : PUBLISH_STATE_COLOR[publishState]
                  }
                />
              </div>
            </div>
          </div>
        }
        backRoute={{
          pathname: "/library/resources",
          query: { tab: ContentTabType.RESOURCES },
        }}
        onPublishClick={handlePublishTrainingResource}
        hasPublishPermission={!!permissions?.hasPublishPermission}
        publishButtonText="Publish"
        publishDisabled={currentStateIsPublished || version === "published"}
        translationModeEnabled={translationModeContext?.translationModeEnabled}
        setTranslationModeEnabled={
          langs.length > 1
            ? translationModeContext.setTranslationModeEnabled
            : undefined
        }
        extraButtons={
          selectedMainTab === "build" && (
            <div className="flex items-center gap-2">
              {props.libraryItem.trainingResource.draftVersion
                .richTextDocument &&
                permissions?.hasEditContentPermission &&
                version !== "published" && <UndoRedoButtons />}
              <TrainingResourceVersionHistoryDropdown />
              <Button onClick={handleDownloadOrExport}>
                <ArrowDownloadIcon />
                Download
              </Button>
            </div>
          )
        }
        overflowItems={
          <>
            <DropdownMenuItem onClick={goToSettingsTab}>
              <GearOutlineIcon className="text-muted-foreground" />
              Settings
            </DropdownMenuItem>
            {trainingResourceId && (
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileArrowInIcon className="text-muted-foreground" />
                Export PDF
              </DropdownMenuItem>
            )}
            {userActionPermissions?.manageLibrary &&
              (props.libraryItem.archivedAt ? (
                <DropdownMenuItem
                  onClick={() => {
                    archiveTrainingResource({
                      variables: {
                        id: props.libraryItem.trainingResource.id,
                        archive: false,
                      },
                    });
                  }}
                >
                  <ArrowRotateCounterclockwiseIcon className="text-muted-foreground" />
                  Unarchive
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => {
                    showModal(
                      <ArchiveConfirmationModal
                        onArchive={() =>
                          archiveTrainingResource({
                            variables: {
                              id: props.libraryItem.trainingResource.id,
                              archive: true,
                            },
                          })
                        }
                        title={"Archive Resource"}
                        itemName={props.libraryItem.name.en}
                        includeModuleCopy
                      />,
                    );
                  }}
                >
                  <ArchiveOutlineIcon className="text-muted-foreground" />
                  Archive
                </DropdownMenuItem>
              ))}
          </>
        }
        onTitleClick={
          permissions?.hasEditContentPermission
            ? editTrainingResourceNameAndDescription
            : undefined
        }
      />
      <LockedTrainingResourceVersionBanner />
    </div>
  );
};

export default TrainingResourcePageHeader;

gql`
  mutation PublishTrainingResource($trainingResourceId: UUID!) {
    publishTrainingResource(trainingResourceId: $trainingResourceId) {
      trainingResource {
        ...TrainingResourceBuilder
      }
      success
    }
  }
  mutation UpdateTrainingResourceNameAndDescription(
    $trainingResourceId: String!
    $input: UpdateTrainingResourceInput!
  ) {
    updateTrainingResource(
      trainingResourceId: $trainingResourceId
      input: $input
    ) {
      success
    }
  }
`;
